import { Injectable, inject, signal } from "@angular/core";
import { Subject } from "rxjs";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";

import { ComposedMessage } from "../ai-composer/ai-composer.component";
import { MessageView, toMessageView } from "../ai-thread/ai-thread.component";
import { AiChatError, AiChatService, ChatEvent, Conversation, PendingAction } from "./ai-chat.service";

const STORAGE_KEY = "eva-ai-drawer-conversation";
/** Nouvel onglet ou F5 : la conversation du galet reprend si elle a servi dans ce délai, sinon on repart à zéro. */
export const RESUME_MS = 2 * 60 * 60 * 1000;
/** Tour retrouvé en cours au chargement : la conversation est relue à ce rythme jusqu'à la réponse. */
const FOLLOW_MS = 3000;

export interface StoredConversation {
  /** Société et collaborateur : une conversation ne passe jamais de l'un à l'autre. */
  owner: string;
  id: string;
  at: number;
}

export function resumableId(stored: StoredConversation | null, owner: string, now: number): string | null {
  return stored && stored.owner === owner && now - stored.at < RESUME_MS ? stored.id : null;
}

/** État d'une conversation : chacune répond à son rythme, l'utilisateur passe de l'une à l'autre pendant ce temps. */
interface ConversationState {
  id: string | null;
  model: string | null;
  messages: MessageView[];
  pending: PendingAction | null;
  busy: boolean;
  progress: string;
  /** Coupe le flux du tour en cours de CETTE conversation (Stop, suppression, changement de société). */
  abort: AbortController;
  /** Numéro du tour : la fin d'un tour arrêté ne touche pas au tour lancé juste après. */
  turn: number;
}

const blank = (id: string | null = null): ConversationState => ({
  id,
  model: null,
  messages: [],
  pending: null,
  busy: false,
  progress: "",
  abort: new AbortController(),
  turn: 0,
});

/**
 * Conversations de l'assistant (galet et page /ai), tenues hors du composant : le galet peut se fermer et l'utilisateur
 * changer de page pendant que l'assistant réfléchit, la réponse arrive ici et s'affiche à la réouverture.
 * (Le tour se termine de toute façon côté agent, déconnexion ou non.)
 * Plusieurs conversations peuvent réfléchir en parallèle (l'agent verrouille par conversation) : la vue montre la
 * conversation courante, les autres continuent et gardent leurs réponses.
 * La courante est retenue dans le localStorage : un nouvel onglet ou un F5 la reprend (voir `RESUME_MS`).
 */
@Injectable({ providedIn: "root" })
export class AiDrawerSession {
  private readonly chat = inject(AiChatService);
  private readonly evaService = inject(EvaService);
  private readonly evaCompanyService = inject(EvaCompanyService);

  private current: ConversationState = blank();
  /** Conversations qui réfléchissent hors de la vue : gardées jusqu'à la fin de leur tour. */
  private readonly running = new Set<ConversationState>();
  /** Dernier chargement demandé : un chargement plus lent qui arrive après ne reprend pas la vue. */
  private opening = 0;

  /** Tour en cours dans la conversation affichée. */
  readonly busy = signal(false);
  /** Tour en cours dans au moins une conversation (onglet Assistant « Réflexion… »). */
  readonly anyBusy = signal(false);
  readonly progress = signal("");
  /** Réponse arrivée galet fermé, ou dans une autre conversation : pastille sur l'onglet Assistant. */
  readonly unread = signal(false);
  /** Vues ouvertes sur la conversation (galet, page /ai ; brièvement les deux en passant de l'un à l'autre). */
  viewers = 0;
  /** Chaque changement d'état : le galet (OnPush) se redessine. */
  readonly changed = new Subject<void>();

  get conversationId(): string | null {
    return this.current.id;
  }
  get model(): string | null {
    return this.current.model;
  }
  get messages(): MessageView[] {
    return this.current.messages;
  }
  get pending(): PendingAction | null {
    return this.current.pending;
  }

  /** Une conversation de la liste réfléchit encore (suppression refusée, pastille d'attente). */
  isBusy(id: string): boolean {
    return this.state(id)?.busy ?? false;
  }

  constructor() {
    this.restore();
    // Une conversation appartient à une société : celle retenue pour la nouvelle société reprend.
    this.evaCompanyService.companyChanged$.subscribe(() => {
      for (const s of [...this.running, this.current]) s.abort.abort();
      this.running.clear();
      this.show(blank());
      this.restore();
    });
  }

  /** Nouvelle conversation ; une conversation en cours continue en arrière-plan. */
  reset(): void {
    this.show(blank());
    this.remember();
  }

  /** Conversation supprimée : son flux est coupé, la vue repart à zéro si c'était elle. */
  forget(id: string): void {
    const s = this.state(id);
    if (!s) return;
    s.abort.abort();
    this.running.delete(s);
    if (s === this.current) this.reset();
    else this.sync();
  }

  /**
   * Stop : l'affichage s'arrête tout de suite, l'agent finit l'étape lancée puis s'arrête (son historique reste
   * cohérent). Une écriture déjà partie n'est pas annulée.
   */
  stop(): void {
    const s = this.current;
    if (!s.busy) return;
    // Sans id, la conversation est en création : rien ne tourne encore côté agent.
    if (s.id) this.chat.stopConversation(s.id).catch(() => undefined); // au pire, le tour va au bout côté agent
    s.abort.abort();
    s.pending = null;
    s.messages.push(toMessageView({ role: "assistant", content: "Réflexion arrêtée à votre demande." }));
    this.finish(s, s.turn);
  }

  /** Conversation à afficher (lien vers /ai) : chargée par le chat à son ouverture. */
  select(id: string): void {
    if (id === this.current.id) return;
    this.show(this.state(id) ?? blank(id));
  }

  /** `onReady` : la conversation existe, la saisie peut être vidée (un échec avant ne perd pas la question). */
  async send({ texte, photos, documents }: ComposedMessage, newModel: string | null, onReady?: () => void) {
    const s = this.current;
    if (s.busy) return;
    const turn = this.start(s);
    const signal = s.abort.signal;
    try {
      if (!s.id) {
        const created = await this.chat.createConversation(newModel);
        s.id = created.id;
        s.model = created.model;
      }
      if (s === this.current) this.remember();
      onReady?.();
      s.pending = null;
      const noms = documents.map((d) => d.nom);
      s.messages.push(toMessageView({ role: "user", content: texte, photos: photos.length, documents: noms }));
      const id = s.id;
      await this.run(s, (sig) => this.chat.send(id, texte, photos, documents, sig), true);
      if (s === this.current) this.remember();
    } catch (e) {
      this.fail(signal, e);
    } finally {
      this.finish(s, turn);
    }
  }

  async decide(action: "valider" | "refuser"): Promise<void> {
    const s = this.current;
    if (!s.id || s.busy) return;
    const id = s.id;
    const answered = s.messages.length;
    s.pending = null;
    const turn = this.start(s);
    const signal = s.abort.signal;
    try {
      // Jeton mort : l'agent remet l'action en attente, la même décision repart après reconnexion.
      await this.run(s, (sig) => this.chat.decide(id, action, sig), true);
    } catch (e) {
      this.fail(signal, e);
    } finally {
      this.finish(s, turn);
    }
    // Ni réponse ni nouvelle carte (erreur) : l'état serveur fait foi, la carte réapparaît si besoin.
    if (!s.pending && s.messages.length === answered && s === this.current) await this.open(id);
  }

  /** Charge une conversation depuis l'agent (historique, retour de /ai, réouverture du galet). */
  async open(id: string): Promise<void> {
    const known = this.state(id);
    // En plein tour : affichée telle quelle, la réponse arrive dans son état.
    if (known?.busy) {
      this.show(known);
      return;
    }
    const s = known ?? blank(id);
    const ticket = ++this.opening;
    try {
      const conversation = await this.chat.conversation(id);
      if (s.busy) return; // un envoi est parti entre-temps : son état fait foi
      // Une autre conversation a été choisie pendant le chargement : elle garde la vue.
      if (s !== this.current && ticket !== this.opening) return;
      this.load(s, conversation);
      this.show(s);
      this.remember();
      if (conversation.in_progress) void this.follow(s);
    } catch (e) {
      // Conversation retenue puis supprimée (depuis /ai, un autre onglet) : on repart à zéro sans erreur.
      if (e instanceof AiChatError && e.errorType === "CONVERSATION_NOT_FOUND") {
        if (s === this.current) this.reset();
      } else this.fail(null, e);
    } finally {
      this.changed.next();
    }
  }

  private load(s: ConversationState, conversation: Conversation): void {
    s.id = conversation.id;
    s.model = conversation.model;
    s.messages = conversation.messages.map(toMessageView);
    const asked = conversation.in_progress;
    if (asked) s.messages.push(toMessageView({ role: "user", content: asked.texte, photos: asked.photos }));
    s.pending = conversation.pending_action;
  }

  /**
   * Tour lancé avant un F5 ou dans un autre onglet : le flux de réponse est perdu, la conversation est relue jusqu'à
   * la fin du tour. Le Stop reste possible (même appel à l'agent qu'en direct).
   */
  private async follow(s: ConversationState): Promise<void> {
    const turn = this.start(s);
    this.setProgress(s, "Réflexion…");
    const signal = s.abort.signal;
    try {
      while (!signal.aborted && s.id) {
        await new Promise((resolve) => setTimeout(resolve, FOLLOW_MS));
        if (signal.aborted || turn !== s.turn) return;
        const conversation = await this.chat.conversation(s.id);
        if (signal.aborted || conversation.in_progress) continue;
        this.load(s, conversation);
        if (!(this.viewers > 0 && s === this.current)) this.unread.set(true);
        return;
      }
    } catch (e) {
      this.fail(signal, e);
    } finally {
      if (signal === s.abort.signal) this.setProgress(s, "");
      this.finish(s, turn);
    }
  }

  private state(id: string): ConversationState | undefined {
    if (this.current.id === id) return this.current;
    return [...this.running].find((s) => s.id === id);
  }

  /** Affiche `s` ; la conversation quittée continue si elle réfléchit, sinon elle est rechargée à la demande. */
  private show(s: ConversationState): void {
    if (s !== this.current) this.opening++;
    if (this.current !== s && this.current.busy) this.running.add(this.current);
    this.running.delete(s);
    this.current = s;
    if (this.viewers) this.unread.set(false);
    this.sync();
  }

  private start(s: ConversationState): number {
    s.busy = true;
    s.abort = new AbortController();
    s.turn++;
    if (s !== this.current) this.running.add(s);
    this.sync();
    return s.turn;
  }

  private finish(s: ConversationState, turn: number): void {
    if (turn !== s.turn || !s.busy) return; // tour déjà arrêté (Stop), ou un autre a pris la suite
    s.busy = false;
    s.progress = "";
    this.running.delete(s);
    this.sync();
  }

  /** Signaux de la vue recalculés depuis la conversation affichée. */
  private sync(): void {
    this.busy.set(this.current.busy);
    this.progress.set(this.current.progress);
    this.anyBusy.set(this.current.busy || [...this.running].some((s) => s.busy));
    this.changed.next();
  }

  /** Dernière activité de la conversation, pour la reprise dans un autre onglet. */
  private remember(): void {
    try {
      if (!this.current.id) localStorage.removeItem(STORAGE_KEY);
      else {
        const stored: StoredConversation = { owner: this.owner(), id: this.current.id, at: Date.now() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      }
    } catch {
      // Stockage indisponible (navigation privée) : la conversation vit seulement dans cet onglet.
    }
  }

  /** L'identifiant seulement : la conversation est chargée à l'ouverture du galet. */
  private restore(): void {
    let id: string | null;
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as StoredConversation | null;
      id = resumableId(stored, this.owner(), Date.now());
    } catch {
      id = null;
    }
    this.show(blank(id));
    if (id) void this.resumeTurn(id);
  }

  /** Tour lancé avant le F5 : suivi tout de suite, galet fermé ou non (onglet « Réflexion… », pastille à la réponse). */
  private async resumeTurn(id: string): Promise<void> {
    try {
      const conversation = await this.chat.conversation(id);
      const s = this.current;
      if (s.id !== id || s.busy || !conversation.in_progress) return;
      this.load(s, conversation);
      void this.follow(s);
    } catch {
      // Rien à suivre : la conversation est chargée normalement à l'ouverture du galet.
    }
  }

  private owner(): string {
    const company = this.evaCompanyService.getCurrentEvaCompany()?.eva_company_id ?? "";
    return `${company}:${this.evaCompanyService.getCurrentEvaEmployee()?.employee_id ?? ""}`;
  }

  private async run(
    s: ConversationState,
    start: (signal: AbortSignal) => AsyncGenerator<ChatEvent>,
    retryOnReconnect: boolean,
  ) {
    this.setProgress(s, "Réflexion…");
    const signal = s.abort.signal;
    try {
      await this.chat.play(start, signal, (event) => this.onEvent(s, signal, event), retryOnReconnect);
    } finally {
      if (signal === s.abort.signal) this.setProgress(s, ""); // pas celle d'un tour relancé après un Stop
    }
  }

  private setProgress(s: ConversationState, label: string): void {
    s.progress = label;
    if (s === this.current) this.progress.set(label);
    this.changed.next();
  }

  private onEvent(s: ConversationState, signal: AbortSignal, event: ChatEvent): void {
    if (signal.aborted) return; // tour arrêté, conversation supprimée ou autre société : la suite est ignorée
    if (event.type === "outil") this.setProgress(s, event.libelle);
    if (event.type === "reponse")
      s.messages.push(toMessageView({ role: "assistant", content: event.texte, turn_id: event.turn_id }));
    if (event.type === "confirmation") s.pending = event.action;
    if (event.type === "erreur")
      s.messages.push(toMessageView({ role: "assistant", content: event.message, error: true }));
    const seen = this.viewers > 0 && s === this.current;
    if (event.type !== "outil" && !seen) this.unread.set(true);
    this.changed.next();
  }

  private fail(signal: AbortSignal | null, e: unknown): void {
    if (signal?.aborted) return; // tour arrêté ou conversation abandonnée : rien à signaler
    const message = e instanceof AiChatError ? e.message : "L'assistant ne répond pas.";
    this.evaService.showError(message, "Assistant", e instanceof AiChatError ? e.errorType : String(e));
  }
}
